import { graphqlRequest } from "./graphql";

// ── API Token ──

interface GetAPITokenResponse {
  whatsub_b2b_client: Array<{ id: string }>;
}

export async function getAPIToken(userId: string, token: string): Promise<string | null> {
  const query = `
    query GetAPIToken($user_id: uuid!) {
      whatsub_b2b_client(where: {user_id: {_eq: $user_id}}) {
        id
      }
    }
  `;
  const data = await graphqlRequest<GetAPITokenResponse>(query, { user_id: userId }, token);
  return data.whatsub_b2b_client[0]?.id ?? null;
}

// ── Generate API Token ──

export interface GeneratedToken {
  clientId: string;
  clientSecret: string;
}

interface GenerateAPITokenResponse {
  newB2BClient: {
    affected_rows: number;
    clientId: string;
    clientSecret: string;
  };
}

export async function generateAPIToken(userId: string, token: string): Promise<GeneratedToken> {
  const query = `
    mutation GenerateAPIToken($user_id: uuid!) {
      newB2BClient(request: {user_id: $user_id}) {
        affected_rows
        clientId
        clientSecret
      }
    }
  `;
  const data = await graphqlRequest<GenerateAPITokenResponse>(query, { user_id: userId }, token);
  return {
    clientId: data.newB2BClient.clientId,
    clientSecret: data.newB2BClient.clientSecret,
  };
}

// ── Whitelisted IPs ──

export interface WhitelistedIPData {
  ip: string;
  created_at: string;
  updated_at: string;
}

interface GetWhitelistedIPsResponse {
  whatsub_b2b_client: WhitelistedIPData[];
}

export async function getWhitelistedIPs(userId: string, token: string): Promise<WhitelistedIPData | null> {
  const query = `
    query GetWhitelistedIPs($user_id: uuid!) {
      whatsub_b2b_client(where: {user_id: {_eq: $user_id}}) {
        ip
        created_at
        updated_at
      }
    }
  `;
  const data = await graphqlRequest<GetWhitelistedIPsResponse>(query, { user_id: userId }, token);
  return data.whatsub_b2b_client[0] ?? null;
}

// ── Update Whitelisted IP ──

interface UpdateWhitelistedIPResponse {
  update_whatsub_b2b_client: {
    affected_rows: number;
    returning: Array<{ ip: string; updated_at: string }>;
  };
}

export async function updateWhitelistedIP(
  userId: string,
  ip: string,
  token: string
): Promise<WhitelistedIPData> {
  const query = `
    mutation UpdateWhitelistedIP($ip: String!, $user_id: uuid!) {
      update_whatsub_b2b_client(
        where: {user_id: {_eq: $user_id}},
        _set: {ip: $ip}
      ) {
        affected_rows
        returning {
          ip
          updated_at
        }
      }
    }
  `;
  const data = await graphqlRequest<UpdateWhitelistedIPResponse>(
    query,
    { ip, user_id: userId },
    token
  );
  const row = data.update_whatsub_b2b_client.returning[0];
  return { ip: row.ip, created_at: "", updated_at: row.updated_at };
}

// ── Webhook Config ──

export interface WebhookConfig {
  webhook: string | null;
  webhook_active: boolean;
  webhook_secret_key: string | null;
  webhook_active_encryption: boolean;
}

interface GetWebhookConfigResponse {
  whatsub_b2b_client: WebhookConfig[];
}

export async function getWebhookConfig(userId: string, token: string): Promise<WebhookConfig | null> {
  const query = `
    query GetWebhookConfig($user_id: uuid!) {
      whatsub_b2b_client(where: {user_id: {_eq: $user_id}}) {
        webhook
        webhook_active
        webhook_secret_key
        webhook_active_encryption
      }
    }
  `;
  const data = await graphqlRequest<GetWebhookConfigResponse>(query, { user_id: userId }, token);
  return data.whatsub_b2b_client[0] ?? null;
}

// ── Update Webhook Config ──

interface UpdateWebhookConfigResponse {
  update_whatsub_b2b_client: { affected_rows: number };
}

export async function updateWebhookConfig(
  userId: string,
  config: {
    webhook: string;
    webhook_active: boolean;
    webhook_secret_key: string;
    webhook_active_encryption: boolean;
  },
  token: string
): Promise<void> {
  const query = `
    mutation UpdateWebhookConfig(
      $user_id: uuid!
      $webhook: String!
      $webhook_active: Boolean!
      $webhook_active_encryption: Boolean!
      $webhook_secret_key: String!
    ) {
      update_whatsub_b2b_client(
        where: {user_id: {_eq: $user_id}}
        _set: {
          webhook: $webhook
          webhook_active: $webhook_active
          webhook_active_encryption: $webhook_active_encryption
          webhook_secret_key: $webhook_secret_key
        }
      ) {
        affected_rows
      }
    }
  `;
  await graphqlRequest<UpdateWebhookConfigResponse>(
    query,
    { user_id: userId, ...config },
    token
  );
}
